import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, X, CornersOut } from '@phosphor-icons/react';
import EdgeStreamAppsOverlay from './EdgeStreamAppsOverlay';

/**
 * Broadcaster-side "Preview as viewer" PIP.
 *
 * - Draggable via framer-motion (constrained to the parent container).
 * - Resizable from the bottom-right corner via a pointer-based handle.
 * - Ratio locked at 16:9 for the video area; header + footer add a fixed vertical offset.
 *
 * All non-visual state (open/close) lives in the parent; size + position are internal.
 */
const MIN_W = 180;
const MAX_W = 640;

const ViewerPreviewPIP = ({
  open,
  onClose,
  localStream,
  streamId,
  leaderboard,
  tokensReceived,
  dragConstraintsRef, // ref to parent container so drag stays inside the video
}) => {
  const videoRef = useRef(null);
  const [width, setWidth] = useState(260);
  const resizing = useRef(false);

  useEffect(() => {
    if (open && videoRef.current && localStream) {
      videoRef.current.srcObject = localStream;
    }
  }, [open, localStream]);

  // Pointer-driven resize from the bottom-right handle
  useEffect(() => {
    const onMove = (e) => {
      if (!resizing.current) return;
      const el = resizing.current.el;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const dx = e.clientX - rect.left;
      const next = Math.max(MIN_W, Math.min(MAX_W, dx));
      setWidth(next);
    };
    const onUp = () => { resizing.current = false; document.body.style.userSelect = ''; };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, []);

  if (!open) return null;

  return (
    <motion.div
      drag
      dragConstraints={dragConstraintsRef}
      dragMomentum={false}
      dragElastic={0}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
      className="absolute top-3 right-3 rounded-xl overflow-hidden border-2 border-blue-500/60 shadow-2xl shadow-blue-500/20 bg-black z-30 touch-none"
      style={{ width }}
      data-testid="viewer-preview-pip"
    >
      {/* Draggable header */}
      <div className="flex items-center justify-between px-2 py-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-[10px] font-bold uppercase tracking-wider cursor-move select-none">
        <span className="flex items-center gap-1">
          <Eye weight="fill" className="w-3 h-3" /> Viewer Preview
          <span className="text-white/60 font-normal normal-case ml-1 text-[9px]">drag · resize ↘</span>
        </span>
        <button
          type="button"
          onClick={onClose}
          onPointerDown={(e) => e.stopPropagation()}
          className="p-0.5 rounded hover:bg-white/20 transition-colors cursor-pointer"
          data-testid="close-viewer-preview-btn"
          aria-label="Close viewer preview"
        >
          <X className="w-3 h-3" />
        </button>
      </div>

      {/* Video + overlay */}
      <div className="relative aspect-video bg-black">
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          className="w-full h-full object-cover pointer-events-none"
          data-testid="viewer-preview-video"
        />
        <EdgeStreamAppsOverlay
          streamId={streamId}
          onTip={undefined}
          leaderboard={leaderboard}
          tokensReceived={tokensReceived}
          compact={true}
        />
      </div>

      {/* Footer + resize handle */}
      <div className="relative px-2 py-1 bg-black/60 text-[9px] text-slate-400 text-center select-none">
        Mirrors what viewers see · Local only
        {/* Resize handle (bottom-right) */}
        <div
          onPointerDown={(e) => {
            e.stopPropagation();
            resizing.current = { el: e.currentTarget.parentElement.parentElement };
            document.body.style.userSelect = 'none';
          }}
          className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize flex items-end justify-end pr-0.5 pb-0.5 text-blue-300 hover:text-white"
          data-testid="pip-resize-handle"
          title="Drag to resize"
          aria-label="Resize preview"
        >
          <CornersOut className="w-3 h-3" />
        </div>
      </div>
    </motion.div>
  );
};

export default ViewerPreviewPIP;
